/**
 * Funnel Widget (Story 5.18, Task 2)
 *
 * Displays a horizontal conversion funnel with progressively narrower segments.
 * Pure SVG rendering (no Recharts — funnels are not a Recharts chart type).
 * Uses shared calculation utilities from @provenleap/types for WYSIWYG PDF parity.
 */

import { useMemo } from "react";
import type { WidgetConfig } from "../../../lib/templateUtils";
import type { FunnelData, WidgetDataEnvelope } from "../../../lib/widgetData";
import { isFunnelData, calculateFunnelMetrics } from "../../../lib/widgetData";
import { calculateTrend, TREND_COLORS } from "@provenleap/types/report-rendering";
import { useChartWidgetShell, ChartMessage, NAVY_SERIES } from "./chartWidgetShell";

/** Navy stage palette for the funnel (matches the chart family). */
const CHART_COLORS = NAVY_SERIES;

interface FunnelWidgetProps {
  widget: WidgetConfig;
  data?: WidgetDataEnvelope<FunnelData>;
}

/**
 * 3-tier responsive degradation by colSpan:
 * - ≤3: simplified (values only, no drop-off labels)
 * - 4–5: compact (shorter labels, drop-off %)
 * - 6+: full (all labels, values, drop-off rates, comparison)
 */
type FunnelTier = "simplified" | "compact" | "full";

function getTier(colSpan: number): FunnelTier {
  if (colSpan <= 3) return "simplified";
  if (colSpan <= 5) return "compact";
  return "full";
}

export function FunnelWidget({ widget, data }: FunnelWidgetProps) {
  const colSpan = widget.position.colSpan;
  const tier = getTier(colSpan);
  const { shell } = useChartWidgetShell(widget.config);

  if (data?.loading) {
    return shell("funnel", widget.title, <div className="h-full min-h-[60px] w-full animate-pulse rounded bg-muted" />);
  }
  if (data?.error) {
    return shell("funnel", widget.title, <ChartMessage role="alert" testId="funnel-error">{data.error}</ChartMessage>, { centered: true });
  }

  const funnelData = data?.data;
  if (!funnelData || !isFunnelData(funnelData)) {
    return shell("funnel", widget.title, <ChartMessage testId="funnel-no-data">No data available</ChartMessage>, { centered: true });
  }

  return shell(
    "funnel",
    widget.title,
    <div className="min-h-0 w-full flex-1">
      <FunnelSvg funnelData={funnelData} tier={tier} />
    </div>,
  );
}

interface FunnelSvgProps {
  funnelData: FunnelData;
  tier: FunnelTier;
}

function FunnelSvg({ funnelData, tier }: FunnelSvgProps) {
  const stages = funnelData.stages;
  const comparisonStages = funnelData.comparisonStages;

  const metrics = useMemo(
    () => calculateFunnelMetrics(stages),
    [stages],
  );

  const comparisonMetrics = useMemo(
    () => comparisonStages ? calculateFunnelMetrics(comparisonStages) : null,
    [comparisonStages],
  );

  const stageCount = metrics.length;
  if (stageCount === 0) return null;

  const maxValue = metrics[0]!.value;

  // SVG layout constants
  const viewWidth = 600;
  const viewHeight = 200;
  const stageGap = 4;
  const labelAreaHeight = tier === "simplified" ? 30 : 40;
  const dropOffAreaHeight = tier === "simplified" ? 0 : 24;
  const funnelTop = labelAreaHeight;
  const funnelHeight = viewHeight - labelAreaHeight - dropOffAreaHeight - 10;
  const totalGaps = (stageCount - 1) * stageGap;
  const stageWidth = (viewWidth - totalGaps) / stageCount;

  // Font sizes by tier
  const labelSize = tier === "simplified" ? 9 : tier === "compact" ? 10 : 11;
  const valueSize = tier === "simplified" ? 11 : tier === "compact" ? 12 : 14;
  const dropOffSize = 9;

  return (
    <svg
      viewBox={`0 0 ${viewWidth} ${viewHeight}`}
      className="w-full h-full"
      role="img"
      aria-label={`Conversion funnel: ${stageCount} stages`}
      preserveAspectRatio="xMidYMid meet"
    >
      {metrics.map((stage, index) => {
        const x = index * (stageWidth + stageGap);
        const widthRatio = maxValue > 0 ? stage.value / maxValue : 0;
        const nextWidthRatio = index < stageCount - 1
          ? (metrics[index + 1]!.value / (maxValue || 1))
          : widthRatio;

        // Trapezoid: narrows from current to next ratio
        const topInset = ((1 - widthRatio) * funnelHeight) / 2;
        const bottomInset = ((1 - nextWidthRatio) * funnelHeight) / 2;

        const points = [
          `${x},${funnelTop + topInset}`,
          `${x + stageWidth},${funnelTop + bottomInset}`,
          `${x + stageWidth},${funnelTop + funnelHeight - bottomInset}`,
          `${x},${funnelTop + funnelHeight - topInset}`,
        ].join(" ");

        const color = CHART_COLORS[index % CHART_COLORS.length]!;
        const centerX = x + stageWidth / 2;
        const centerY = funnelTop + funnelHeight / 2;

        // Comparison trend
        const compStage = comparisonMetrics?.[index];
        const trend = compStage ? calculateTrend(stage.value, compStage.value) : null;

        const formattedValue = new Intl.NumberFormat("en-US", {
          notation: stage.value >= 1_000_000 ? "compact" : "standard",
          maximumFractionDigits: 1,
        }).format(stage.value);

        return (
          <g key={stage.label} role="img" aria-label={`${stage.label}: ${formattedValue}, ${stage.conversionRate.toFixed(1)}% conversion`}>
            {/* Trapezoid segment */}
            <polygon points={points} fill={color} opacity={0.85} />

            {/* Stage label (above funnel) */}
            <text
              x={centerX}
              y={funnelTop - (tier === "simplified" ? 8 : 14)}
              textAnchor="middle"
              fontSize={labelSize}
              fill="#6b7280"
              fontFamily="Inter, sans-serif"
            >
              {tier === "simplified" && stage.label.length > 8
                ? stage.label.slice(0, 7) + "…"
                : tier === "compact" && stage.label.length > 14
                  ? stage.label.slice(0, 13) + "…"
                  : stage.label}
            </text>

            {/* Absolute value (inside segment) */}
            <text
              x={centerX}
              y={centerY + (trend && tier === "full" ? -6 : 2)}
              textAnchor="middle"
              fontSize={valueSize}
              fontWeight="bold"
              fill="#fff"
              fontFamily="JetBrains Mono, monospace"
            >
              {formattedValue}
            </text>

            {/* Comparison trend (below value, inside segment) */}
            {trend && trend.direction !== "neutral" && tier === "full" && (
              <text
                x={centerX}
                y={centerY + 14}
                textAnchor="middle"
                fontSize={10}
                fill={trend.direction === "up" ? TREND_COLORS.up : TREND_COLORS.down}
                fontFamily="Inter, sans-serif"
              >
                {trend.direction === "up" ? "↑" : "↓"} {trend.percentage.toFixed(1)}%
              </text>
            )}

            {/* Drop-off indicator between stages */}
            {index > 0 && tier !== "simplified" && (
              <g>
                <circle
                  cx={x - stageGap / 2}
                  cy={funnelTop + funnelHeight + 12}
                  r={4}
                  fill={stage.retentionColor}
                />
                {tier === "full" && (
                  <text
                    x={x - stageGap / 2 + 8}
                    y={funnelTop + funnelHeight + 15}
                    fontSize={dropOffSize}
                    fill="#6b7280"
                    fontFamily="Inter, sans-serif"
                  >
                    {stage.dropOffRate.toFixed(0)}% drop
                  </text>
                )}
                {tier === "compact" && (
                  <text
                    x={x - stageGap / 2 + 8}
                    y={funnelTop + funnelHeight + 15}
                    fontSize={dropOffSize}
                    fill="#6b7280"
                    fontFamily="Inter, sans-serif"
                  >
                    {stage.dropOffRate.toFixed(0)}%
                  </text>
                )}
              </g>
            )}
          </g>
        );
      })}
    </svg>
  );
}
